import { getCookie, setCookie } from '@/lib/cookies';

/** À garder aligné avec le script inline dans `index.html`. */
export const LYRRA_CONSENT_STORAGE_KEY = 'lyrra_consent_v1';
export const LYRRA_CONSENT_DETAIL_KEY = 'lyrra_consent_detail_v1';

/** Nom du cookie décrit sur la page Cookies (politique). */
export const LYRRA_CONSENT_COOKIE_NAME = 'cookieConsent';

export const LYRRA_CONSENT_CHANGED_EVENT = 'lyrra-consent-changed';

/** Résumé stocké (compat index.html + cookie politique). */
export type LyrraConsentChoice = 'all' | 'essential' | 'custom';

/** Préférences granulaires (effet réel sur Consent Mode / scripts). */
export type LyrraConsentPrefs = {
  analytics: boolean;
  marketing: boolean;
};

export function prefsFromChoice(choice: LyrraConsentChoice): LyrraConsentPrefs {
  if (choice === 'all') return { analytics: true, marketing: true };
  if (choice === 'essential') return { analytics: false, marketing: false };
  return readConsentDetail() ?? { analytics: false, marketing: false };
}

export function choiceFromPrefs(prefs: LyrraConsentPrefs): LyrraConsentChoice {
  if (prefs.analytics && prefs.marketing) return 'all';
  if (!prefs.analytics && !prefs.marketing) return 'essential';
  return 'custom';
}

function readConsentDetail(): LyrraConsentPrefs | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LYRRA_CONSENT_DETAIL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LyrraConsentPrefs>;
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.marketing !== 'boolean') return null;
    return { analytics: parsed.analytics, marketing: parsed.marketing };
  } catch {
    return null;
  }
}

export function readStoredConsent(): LyrraConsentChoice | null {
  if (typeof window === 'undefined') return null;
  try {
    const fromLs = localStorage.getItem(LYRRA_CONSENT_STORAGE_KEY);
    if (fromLs === 'all' || fromLs === 'essential' || fromLs === 'custom') return fromLs;
  } catch {
    /* private mode, etc. */
  }
  const fromCookie = getCookie(LYRRA_CONSENT_COOKIE_NAME);
  if (fromCookie === 'all' || fromCookie === 'essential' || fromCookie === 'custom') return fromCookie;
  return null;
}

export function readStoredConsentPrefs(): LyrraConsentPrefs | null {
  const choice = readStoredConsent();
  if (!choice) return null;
  return prefsFromChoice(choice);
}

export function persistConsent(choice: LyrraConsentChoice): void {
  const prefs = prefsFromChoice(choice);
  persistConsentPrefs(prefs);
}

export function persistConsentPrefs(prefs: LyrraConsentPrefs): void {
  const choice = choiceFromPrefs(prefs);
  try {
    localStorage.setItem(LYRRA_CONSENT_STORAGE_KEY, choice);
    localStorage.setItem(LYRRA_CONSENT_DETAIL_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
  setCookie(LYRRA_CONSENT_COOKIE_NAME, choice, 365);
  applyGtagConsentPrefs(prefs);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(LYRRA_CONSENT_CHANGED_EVENT, { detail: prefs }));
  }
}

export function applyGtagConsent(choice: LyrraConsentChoice): void {
  applyGtagConsentPrefs(prefsFromChoice(choice));
}

export function applyGtagConsentPrefs(prefs: LyrraConsentPrefs): void {
  const g = typeof window !== 'undefined' ? window.gtag : undefined;
  if (typeof g !== 'function') return;

  g('consent', 'update', {
    ad_storage: prefs.marketing ? 'granted' : 'denied',
    ad_user_data: prefs.marketing ? 'granted' : 'denied',
    ad_personalization: prefs.marketing ? 'granted' : 'denied',
    analytics_storage: prefs.analytics ? 'granted' : 'denied',
    functionality_storage: prefs.analytics ? 'granted' : 'denied',
    personalization_storage: prefs.marketing ? 'granted' : 'denied',
  });
}
